<template>
  <div class="step-container">
    <!-- Header Section -->
    <StepHeader :currentStep="currentStep" :steps="steps" />

    <!-- Content Section -->
    <div class="content-section">

      <!-- Brand Filter Tags -->
      <div class="brand-filters">
        <div
          v-for="bv in brandValidation"
          :key="bv.CompetitorCarBrand"
          class="brand-tag"
          v-if="bv.CompetitorCarBrand"
        >
          <span class="brand-name">
            {{ bv.CompetitorCarBrand }} ({{ bv.Distribution }}%)
          </span>
        </div>
      </div>

      <!-- Vehicles Table -->
      <div class="wizard-table-container">
        <!-- Table Header -->
        <div class="wizard-table-header">
          <div style="text-align: left;">{{ lang.W00044 }}</div>
          <div>{{ lang.W00045 }}</div>
          <div>{{ lang.W00041 }}</div>
          <div></div>
        </div>

        <!-- Table Rows -->
        <div class="wizard-table-body" v-if="competitorCarModelDistribution">
          <div
            v-for="(e, index) in competitorCarModelDistribution"
            :key="index"
            class="wizard-table-row"
            :class="{
              'invalid-wizard-table-row': !isBrandValid(e.CompetitorCarBrand)
            }"
          >
            <div class="wizard-table-cell">
              <select class="wizard-select" v-model="e.CompetitorCarBrand" :disabled="readonly">
                <option :value="null" disabled hidden>{{ lang.W00047 }}</option>
                <option
                  v-for="brand in brands"
                  :key="brand"
                  :value="brand"
                >
                  {{ brand }}
                </option>
              </select>
            </div>
            <div class="wizard-table-cell">
              <input class="wizard-input" type="text" v-model="e.CompetitorCarModel" :disabled="readonly">
            </div>
            <div class="wizard-table-cell">
              <input class="wizard-input" type="number" v-model="e.Distribution" :min="0" :max="100" :step="0.1" :disabled="readonly"></input>
            </div>
            <div class="wizard-table-cell">
              <button class="delete-vehicle-btn" @click="deleteVehicle(e)" :disabled="readonly">
                <i class="material-icons md-24" >delete</i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="add-vehicle-btn-container">
        <button class="add-vehicle-btn" @click="addVehicle()" :disabled="readonly">
          <i class="material-icons md-24">add</i>
          <span>{{ lang.W00046 }}</span>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import "../../../styles/audi-steps.css";
import StepHeader from './StepHeader.vue';
import WizardService from "../../../services/WizardService";

export default {
  name: 'Step4',
  components: {
    StepHeader
  },
  props: {
    formData: { type: Object, required: true },
    currentStep: { type: Number, default: 0 },
    steps: { type: Array, required: true },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      localFormData: structuredClone(this.formData),
      brands: null,
      competitorCarModelDistribution: null,
      brandValidation: [],
      lang: {
        W00041: "Share (%)",
        W00044: "Brand",
        W00045: "Model",
        W00046: "Add vehicle",
        W00047: "Select a brand…",
      }
    }
  },
  watch: {
    "formData": {
      deep: true,
      handler() {
        this.localFormData = structuredClone(this.formData);
      }
    },
    "localFormData": {
      deep: true,
      handler(newVal) {
        // this.$emit('update:formData', newVal); // is emitted in watcher for competitorCarModelDistribution
      }
    },
    "competitorCarModelDistribution": {
      handler(newValue, oldValue) {
        this.brandValidation = Object.values(
          this.competitorCarModelDistribution.reduce((acc, item) => {
            if (!acc[item.CompetitorCarBrand]) {
              acc[item.CompetitorCarBrand] = { CompetitorCarBrand: item.CompetitorCarBrand, Distribution: 0 };
            }
            acc[item.CompetitorCarBrand].Distribution += Number(item.Distribution);
            return acc;
          }, {})
        );

        let competitorCarModelDistribution = structuredClone(this.competitorCarModelDistribution);
        competitorCarModelDistribution = competitorCarModelDistribution.filter(e => e.CompetitorCarBrand && e.CompetitorCarModel);

        for (const e of competitorCarModelDistribution) {
          e.Distribution = (e.Distribution / 100).toFixed(4);
        }
        this.localFormData.competitorCarModelDistribution = competitorCarModelDistribution;
        this.$emit('update:formData', this.localFormData);
      },
      deep: true
    },
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this);
    },
  },
  async created() {
    if(!this.localFormData.competitorCarModelDistribution) this.$set(this.localFormData, 'competitorCarModelDistribution', []);
    let competitorCarModelDistribution = structuredClone(this.localFormData.competitorCarModelDistribution);
    for (const e of competitorCarModelDistribution) {
      e.Distribution = Number((e.Distribution * 100).toFixed(4));
    }
    this.competitorCarModelDistribution = competitorCarModelDistribution;

    let res = await WizardService.readBrandsOfConfiguration(this.localFormData.configurationGuid)
    if(res) this.brands = res.map(p => p.LocationPropertyValue);
  },
  mounted() {
    if (this.globals.selectedLanguage) { 
      this.globals.getLanguageStrings(this);
    }
  },
  methods: {
    isBrandValid(competitorCarBrand) {
      if(this.brandValidation.some(e => (e.CompetitorCarBrand == competitorCarBrand && e.Distribution != 100))) return false;
      return true;
    },
    validateStep() {
      this.competitorCarModelDistribution = this.competitorCarModelDistribution.filter(e => e.CompetitorCarBrand && e.CompetitorCarModel);

      for (const e of this.brandValidation) {
        if(e.Distribution != 100) {
          return { valid: false, msg: `The sum of the share of ${e.CompetitorCarBrand} must be 100%.`};
        }
      }

      if(this.hasDuplicateBrandModel()) {
        return { valid: false, msg: `Saving failed: Model name duplicated.`};
      }

      return { valid: true, msg: null};
    },
    addVehicle() {
      let v = this.competitorCarModelDistribution.find(e => 
        !e.CompetitorCarBrand && 
        !e.CompetitorCarModel &&
        !e.Distribution);

      if(v) return;

      this.competitorCarModelDistribution.push({
        CompetitorCarBrand: null,
        CompetitorCarModel: null,
        Distribution: 0
      });
    },
    deleteVehicle(v) {
      this.competitorCarModelDistribution = this.competitorCarModelDistribution.filter(e => e != v);
    },
    hasDuplicateBrandModel() {
      const seen = new Set();

      for (const item of this.competitorCarModelDistribution) {
        const key = `${item.CompetitorCarBrand.toLowerCase().trim()}|${item.CompetitorCarModel.toLowerCase().trim()}`;
        if (seen.has(key)) {
          return true;
        }
        seen.add(key);
      }
      return false;
    }
  }
}
</script>

<style lang="css" scoped>

.vehicles-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 1280px) {
    grid-template-columns: 2fr 1fr;
    gap: 48px;
  }
}

.vehicles-main {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.field-description {
  font-size: 14px;
  color: #666666;
}

.brand-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.brand-tag {
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
  padding: 4px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
}

.brand-name {
  font-size: 14px;
  font-weight: 500;
  color: #333333;
}


.wizard-table-header {
  grid-template-columns: 1fr 2fr 1fr 0.2fr;
}

.wizard-table-row {
  grid-template-columns: 1fr 2fr 1fr 0.2fr;
}


.add-vehicle-btn {
  background: var(--color-primary);
  border-radius: 6px;
  border: none;
  width: 150px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.5;
    background: #e0e0e0;
    color: #666666;
    cursor: not-allowed;
    cursor: not-allowed;
  }
}

.add-vehicle-btn-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
}

.delete-vehicle-btn {
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:disabled {
    cursor: not-allowed;
  }
}
</style>



// WEBPACK FOOTER //
// Step4.vue?2238b182